import { Suspense, lazy, type ReactNode } from "react";
import { Navigate, Outlet, RouterProvider, createHashRouter, useParams } from "react-router-dom";
import { AuthPage, RecoveryPage } from "../features/auth/AuthPage";
import { useSession } from "../features/auth/SessionProvider";
import { AccountProvider, useAccount } from "../features/platform/AccountProvider";
import { can } from "../features/platform/model/access";
import { AssistantApiProvider } from "../features/assistant/AssistantApiProvider";
import { AppShell } from "./AppShell";
import { DomainProviders } from "./DomainProviders";
import { ErrorBoundary } from "./ErrorBoundary";
import { ProfileSubnav } from "../features/profiles/ProfileSubnav";

const LandingPage = lazy(async () => ({
  default: (await import("../pages/LandingPage")).LandingPage,
}));
const CalculatorsPage = lazy(async () => ({
  default: (await import("../pages/CalculatorsPage")).CalculatorsPage,
}));
const HomePage = lazy(async () => ({ default: (await import("../pages/HomePage")).HomePage }));
const ProfilePage = lazy(async () => ({
  default: (await import("../pages/ProfilePage")).ProfilePage,
}));
const PersonalProfileRedirect = lazy(async () => ({
  default: (await import("../pages/ProfilePage")).PersonalProfileRedirect,
}));
const ProfilesPage = lazy(async () => ({
  default: (await import("../pages/ProfilesPage")).ProfilesPage,
}));
const SettingsPage = lazy(async () => ({
  default: (await import("../pages/SettingsPage")).SettingsPage,
}));
const AssistantPage = lazy(async () => ({
  default: (await import("../features/assistant/AssistantPage")).AssistantPage,
}));
// Cada domínio da Fase 4 vira um chunk próprio, carregado apenas na sua rota.
const ProtocolsPage = lazy(async () => ({
  default: (await import("../features/protocols/ProtocolsPage")).ProtocolsPage,
}));
const ExamsPage = lazy(async () => ({
  default: (await import("../features/exams/ExamsPage")).ExamsPage,
}));
const BodyPage = lazy(async () => ({
  default: (await import("../features/body/BodyPage")).BodyPage,
}));
const TrainingPage = lazy(async () => ({
  default: (await import("../features/training/TrainingPage")).TrainingPage,
}));
const NutritionPage = lazy(async () => ({
  default: (await import("../features/nutrition/NutritionPage")).NutritionPage,
}));
const ProgressPage = lazy(async () => ({
  default: (await import("../pages/ProgressPage")).ProgressPage,
}));
const ReportPage = lazy(async () => ({
  default: (await import("../features/reports/ReportPage")).ReportPage,
}));

function Deferred({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="route-loading" aria-busy="true">
          Carregando visualização…
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

function RootGate() {
  const { status } = useSession();
  if (status === "loading")
    return (
      <main className="center-state" aria-busy="true">
        Carregando…
      </main>
    );
  if (status === "authenticated") return <Navigate to="/inicio" replace />;
  return <LandingPage />;
}

function ProtectedAccount() {
  const { status, recovery } = useSession();
  if (status === "loading")
    return (
      <main className="center-state" aria-busy="true">
        Recuperando sessão…
      </main>
    );
  if (recovery) return <Navigate to="/recovery" replace />;
  if (status === "anonymous") return <Navigate to="/auth" replace />;
  return (
    <AccountProvider>
      <AssistantApiProvider>
        <DomainProviders>
          <Outlet />
        </DomainProviders>
      </AssistantApiProvider>
    </AccountProvider>
  );
}

const PHASE4_PAGES = {
  protocols: ProtocolsPage,
  exams: ExamsPage,
  nutrition: NutritionPage,
  training: TrainingPage,
  body: BodyPage,
  progress: ProgressPage,
} as const;

function ProfileTabRoute() {
  const { profileId, tab } = useParams();
  const { access, enabledModules } = useAccount();
  const allowedTabs = {
    protocols: "protocolos",
    exams: "exames",
    nutrition: "nutricao",
    training: "treino",
    body: "corpo",
    progress: "evolucao",
  } as const;
  if (profileId === undefined || tab === undefined) return <Navigate to="/inicio" replace />;
  const workspace = (content: ReactNode) => (
    <div className="profile-workspace">
      <ProfileSubnav />
      <Deferred>{content}</Deferred>
    </div>
  );
  if (tab === "overview") return workspace(<ProfilePage />);
  if (tab === "report") {
    if (!can(access, "report_pdf")) return <Navigate to={`/aluno/${profileId}/overview`} replace />;
    return workspace(<ReportPage />);
  }
  if (tab === "chats") {
    if (!enabledModules(profileId).has("assistente"))
      return <Navigate to={`/aluno/${profileId}/overview`} replace />;
    return workspace(<AssistantPage />);
  }
  const module = allowedTabs[tab as keyof typeof allowedTabs];
  if (module === undefined) return <Navigate to={`/aluno/${profileId}/overview`} replace />;
  // Módulo desabilitado continua legível: o histórico permanece e apenas a
  // escrita some. Só `evolucao` e `corpo` compartilham a mesma página.
  const Page = PHASE4_PAGES[tab as keyof typeof PHASE4_PAGES];
  return workspace(<Page />);
}

const router = createHashRouter([
  {
    path: "/",
    element: (
      <Deferred>
        <RootGate />
      </Deferred>
    ),
  },
  {
    path: "/calculadoras",
    element: (
      <Deferred>
        <CalculatorsPage />
      </Deferred>
    ),
  },
  { path: "/auth", element: <AuthPage /> },
  { path: "/recovery", element: <RecoveryPage /> },
  {
    element: <ProtectedAccount />,
    children: [
      {
        element: <AppShell />,
        children: [
          {
            path: "inicio",
            element: (
              <Deferred>
                <HomePage />
              </Deferred>
            ),
          },
          {
            path: "chat",
            element: (
              <Deferred>
                <AssistantPage />
              </Deferred>
            ),
          },
          {
            path: "chat/:conversationId",
            element: (
              <Deferred>
                <AssistantPage />
              </Deferred>
            ),
          },
          {
            path: "alunos",
            element: (
              <Deferred>
                <ProfilesPage />
              </Deferred>
            ),
          },
          { path: "profiles", element: <Navigate to="/alunos" replace /> },
          {
            path: "meu-perfil",
            element: (
              <Deferred>
                <PersonalProfileRedirect />
              </Deferred>
            ),
          },
          { path: "aluno/:profileId/:tab", element: <ProfileTabRoute /> },
          {
            path: "aluno/:profileId/chats/:conversationId",
            element: (
              <Deferred>
                <AssistantPage />
              </Deferred>
            ),
          },
          {
            path: "configuracoes",
            element: (
              <Deferred>
                <SettingsPage />
              </Deferred>
            ),
          },
        ],
      },
    ],
  },
  { path: "*", element: <Navigate to="/" replace /> },
]);

export function App() {
  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  );
}
